import { ActionMeta, GroupBase, MenuListProps, OnChangeValue, StylesConfig } from 'react-select';
import { StyledSelect, CustomSelectWrap } from './style';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useMemo, useRef } from 'react';
import CustomMenuList from './components/customMenuList';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { setEditingMap, toggleMapVisibility } from 'store/slices/CustomMapSlice';
import { useBulkUpdateCustomMap } from 'hooks/api/customMap/useUpdateCustomMap';
import { debounce } from '../../util';
import CustomOption from './components/customOption';
import CustomValueContainer from './components/customValueContainer';
import CustomLoading from './components/customLoading';
import CustomClearIndicator from './components/customClearIndicator';
import CustomMapUploadInput from './components/customMapUploadInput';
import { Location } from 'assets/svg';
import { selectCustomMap } from 'store/selectors/customMapSelector';
import { selectActiveAction, TOOL_ACTION } from 'store/selectors/toolSelector';

export type OptionType = { value: number, label: string }
export const ADD_OPTION: OptionType = { value: -1, label: 'Add' };

export default function CustomMapSelect() {
	const dispatch = useDispatch();
	const { uuid } = useSelector((rs: RootState) => rs.roofData.data);
	const { customMaps, selectedMap } = useSelector(selectCustomMap);
	const activeToolAction = useSelector(selectActiveAction);

	// Loaders
	const isUploadingInProgress = useQueriesRunningInBackGround([`upload-custom-map-${uuid}`], 'mutating');
	const isFileEditingInProgess = useQueriesRunningInBackGround([
		`delete-custom-map-${uuid}`,
		`update-custom-map-${uuid}`,
		`bulk-update-custom-map-${uuid}`
	], 'mutating');

	const loader = isUploadingInProgress || isFileEditingInProgess;

	// Disable select:
	// const disableOption = (activeToolAction && activeToolAction !== TOOL_ACTION.EDIT_CUSTOM_IMAGE
	// 	|| isFileEditingInProgess 
	// 	|| isUploadingInProgress
	// );
	// const fileInputRef = useRef<HTMLInputElement>(null);

	const disableEditImg = (!!activeToolAction && activeToolAction !== TOOL_ACTION.EDIT_CUSTOM_IMAGE);

	const options = useMemo(() => [...customMaps].map((m) => ({
		value: m.id,
		label: m.fileName,
	})), [customMaps]);

	const selectedOptions = useMemo(() =>
		options.filter(o => !!customMaps.find(m => m.id === o.value)?.isVisible),
	[customMaps, options]);

	const pendingVisibilityChanges = useRef<{ [id: number]: boolean }>({});

	const { mutateAsync: updateVisibility } = useBulkUpdateCustomMap(uuid);

	const flushPendingChanges = useMemo(
		() =>
			debounce(() => {
				const entries = Object.entries(pendingVisibilityChanges.current);
				if (!entries.length) return;

				const payload = entries.map(([id, isVisible]) => ({
					id: Number(id),
					isVisible,
				}));

				updateVisibility(payload);

				// reset queue
				pendingVisibilityChanges.current = {};
			}, 1000),
		[updateVisibility]
	);

	const handleSelection = (
		newValue: OnChangeValue<OptionType, true>,
		actionMeta: ActionMeta<OptionType>
	) => {
		if (actionMeta.option?.value === -1) return;
		if (!actionMeta.option && actionMeta.action !== 'clear') return;

		if (actionMeta.action === 'clear') {
			customMaps.forEach((map) => {
				dispatch(toggleMapVisibility({ id: map.id, isVisible: false }));
				pendingVisibilityChanges.current[map.id] = false;
			});
			dispatch(setEditingMap(null));
			flushPendingChanges();
			return;
		}

		const id = actionMeta.option!.value;

		const isVisible = actionMeta.action === 'select-option';
		dispatch(toggleMapVisibility({ id, isVisible }));

		if (selectedMap && selectedMap.id === id && !isVisible) {
			dispatch(setEditingMap(null));
		}

		pendingVisibilityChanges.current[id] = isVisible;
		flushPendingChanges();
	};

	const customStyles: StylesConfig<OptionType, true, GroupBase<OptionType>> = {
		control: (base) => ({
			...base,
			paddingLeft: '28px',
		}),
		option: (base, props) => {
			let backgroundColor = base.backgroundColor;

			// eslint-disable-next-line react/prop-types
			if (props.isFocused) backgroundColor = 'var(--selectDropdownHoverColor)';
			// eslint-disable-next-line react/prop-types
			if (props.isSelected) backgroundColor = 'var(--selectedBgColor)';

			return {
				...base,
				padding: 0,      
				backgroundColor,
				// eslint-disable-next-line react/prop-types
				cursor: props.isDisabled ? 'not-allowed' : 'move',
				// eslint-disable-next-line react/prop-types
				color: props.isDisabled ? 'var(--secondaryTextColor)' : 'var(--textHeadingColor)',
				':active': {
					...base[':active'],
					backgroundColor,
				},
			};
		},
	};

	return (
		<div style={{ width: '100%' }}>
			<CustomMapUploadInput />
			<CustomSelectWrap>
				<div className="select-icon">
					<Location />
				</div>
				<StyledSelect
					styles={customStyles}
					// menuIsOpen={true}
					isMulti={true}
					placeholder='Custom Map'
					// isOptionDisabled={() => disableOption}
					isLoading={loader}
					options={[...options, ADD_OPTION]}
					menuPlacement={'top'}
					closeMenuOnSelect={false}
					hideSelectedOptions={false}
					blurInputOnSelect={false}
					value={selectedOptions}
					onChange={handleSelection}
					isSearchable={false}
					classNamePrefix="custom-map-select"
					components={{
						MenuList: (props: MenuListProps<OptionType>) => (
							<CustomMenuList
								{...props}
								items={options}
							/>
						),
						Option: (props) => (
							<CustomOption
								{...props}
								items={options}
								isEditDisabled={disableEditImg}
								// isDisabled={disableOption}
							/>
						),
						ValueContainer: CustomValueContainer,
						MultiValue: () => null,
						ClearIndicator: CustomClearIndicator,
						LoadingIndicator: CustomLoading,
						
					}}
				/>
			</CustomSelectWrap>

		</div>
	);
}
